import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';

interface FloatingPanelContextValue {
  title: string;
  open: boolean;
  minimized: boolean;
  onMinimize: () => void;
  onClose: () => void;
  // Unconditionally collapses the panel back to its minimized (header/FAB-only) state —
  // unlike onMinimize (a toggle meant for the header's own expand/collapse click), this is for
  // callers that know they want it collapsed regardless of its current state, e.g. a "View all"
  // footer action navigating away, where staying expanded over the destination page would be
  // wrong no matter whether the panel happened to be expanded or already collapsed.
  collapse: () => void;
}

interface FloatingPanelProviderProps {
  title: string;
  defaultMinimized?: boolean;
  children: ReactNode;
}

const FloatingPanelContext = createContext<FloatingPanelContextValue | undefined>(undefined);

// Generic open/minimized/title state for any floating widget — not notification-specific.
// FloatingPanel.tsx and NotificationPanelHeader.tsx both read this via useFloatingPanel(),
// so the same shell/header could host a different FloatingPanel-based feature later.
// `onClose` is a one-way session dismissal (nothing sets `open` back to true), matching the
// "hidden until next page load, not persisted" requirement — no storage write happens here.
export function FloatingPanelProvider({
  title,
  defaultMinimized = true,
  children,
}: FloatingPanelProviderProps) {
  //   const [open, setOpen] = useState(true);
  const [open, setOpen] = useState(() => {
    return sessionStorage.getItem('floating-panel-closed') !== 'true';
  });
  const [minimized, setMinimized] = useState(defaultMinimized);

  const value: FloatingPanelContextValue = {
    title,
    open,
    minimized,
    onMinimize: () => setMinimized((prev) => !prev),
    onClose: () => {
      sessionStorage.setItem('floating-panel-closed', 'true');
      setOpen(false);
    },
    collapse: () => setMinimized(true),
  };

  return <FloatingPanelContext.Provider value={value}>{children}</FloatingPanelContext.Provider>;
}

export function useFloatingPanel(): FloatingPanelContextValue {
  const ctx = useContext(FloatingPanelContext);
  if (!ctx) {
    throw new Error('useFloatingPanel must be used within a FloatingPanelProvider');
  }
  return ctx;
}
